import { DynamoDBClient, PutItemCommand, QueryCommand, QueryCommandInput, UpdateItemCommand } from '@aws-sdk/client-dynamodb';
import { marshall, unmarshall } from '@aws-sdk/util-dynamodb';
import moment from 'moment-timezone';
import { ENVIRONMENT_VARIABLES } from 'src/constants/EnvironmentConstants';
import { CognitoService } from 'src/services/aws/CognitoService';
import { KINGPIN_GOALS_TABLE, REGISTRY_SK } from '../constants/KingpinTableConstants';

export interface KingpinGoalRegistry {
  PK: string;
  SK: string;
  goalId: string;
  tier?: string;
  title?: string;
  tobiasDirectAlias?: string;
  pocAliases?: string[];
  isActive?: boolean;
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export interface KingpinGoalInput {
  goalId: string;
  tier?: string;
  title?: string;
  tobiasDirectAlias?: string;
  pocAliases?: string[];
  isActive?: boolean;
  updatedBy: string;
}

let ddbClient: DynamoDBClient | null = null;

const getClient = (): DynamoDBClient => {
  if (!ddbClient) {
    ddbClient = new DynamoDBClient({
      region: ENVIRONMENT_VARIABLES.env.Region,
      credentials: CognitoService.credentialProvider
    });
  }
  return ddbClient;
};

export class KingpinAdminService {
  static async listGoals(): Promise<KingpinGoalRegistry[]> {
    const client = getClient();
    const items: KingpinGoalRegistry[] = [];
    let exclusiveStartKey: QueryCommandInput['ExclusiveStartKey'];

    do {
      const params: QueryCommandInput = {
        TableName: KINGPIN_GOALS_TABLE,
        IndexName: 'SK-PK-index',
        KeyConditionExpression: 'SK = :sk AND begins_with(PK, :pkPrefix)',
        ExpressionAttributeValues: {
          ':sk': { S: REGISTRY_SK },
          ':pkPrefix': { S: 'GOAL#' }
        },
        ExclusiveStartKey: exclusiveStartKey
      };
      const res = await client.send(new QueryCommand(params));
      res.Items?.forEach((item) => items.push(unmarshall(item) as KingpinGoalRegistry));
      exclusiveStartKey = res.LastEvaluatedKey;
    } while (exclusiveStartKey);

    return items;
  }

  static async createGoal(input: KingpinGoalInput): Promise<KingpinGoalRegistry> {
    const client = getClient();
    const now = moment().toISOString();
    const item: KingpinGoalRegistry = {
      PK: `GOAL#${input.goalId}`,
      SK: REGISTRY_SK,
      goalId: input.goalId,
      tier: input.tier ?? '',
      title: input.title ?? '',
      tobiasDirectAlias: input.tobiasDirectAlias ?? '',
      pocAliases: input.pocAliases ?? [],
      isActive: input.isActive ?? true,
      createdAt: now,
      createdBy: input.updatedBy,
      updatedAt: now,
      updatedBy: input.updatedBy
    };

    await client.send(
      new PutItemCommand({
        TableName: KINGPIN_GOALS_TABLE,
        Item: marshall(item, { removeUndefinedValues: true }),
        ConditionExpression: 'attribute_not_exists(PK)'
      })
    );

    return item;
  }

  static async updateGoal(input: KingpinGoalInput): Promise<void> {
    const client = getClient();
    const now = moment().toISOString();

    const updateExpressions: string[] = ['updatedAt = :updatedAt', 'updatedBy = :updatedBy'];
    const expressionValues: Record<string, unknown> = { ':updatedAt': now, ':updatedBy': input.updatedBy };
    const expressionNames: Record<string, string> = {};

    if (input.tier !== undefined) {
      updateExpressions.push('#tier = :tier');
      expressionValues[':tier'] = input.tier;
      expressionNames['#tier'] = 'tier';
    }
    if (input.title !== undefined) {
      updateExpressions.push('title = :title');
      expressionValues[':title'] = input.title;
    }
    if (input.tobiasDirectAlias !== undefined) {
      updateExpressions.push('tobiasDirectAlias = :tobiasDirectAlias');
      expressionValues[':tobiasDirectAlias'] = input.tobiasDirectAlias;
    }
    if (input.pocAliases !== undefined) {
      updateExpressions.push('pocAliases = :pocAliases');
      expressionValues[':pocAliases'] = input.pocAliases;
    }
    if (input.isActive !== undefined) {
      updateExpressions.push('isActive = :isActive');
      expressionValues[':isActive'] = input.isActive;
    }

    await client.send(
      new UpdateItemCommand({
        TableName: KINGPIN_GOALS_TABLE,
        Key: marshall({ PK: `GOAL#${input.goalId}`, SK: REGISTRY_SK }),
        UpdateExpression: `SET ${updateExpressions.join(', ')}`,
        ConditionExpression: 'attribute_exists(PK)',
        ExpressionAttributeValues: marshall(expressionValues, { removeUndefinedValues: true }),
        ...(Object.keys(expressionNames).length > 0 && { ExpressionAttributeNames: expressionNames })
      })
    );
  }

  static async deleteGoal(goalId: string, updatedBy: string): Promise<void> {
    const client = getClient();
    const now = moment().toISOString();

    await client.send(
      new UpdateItemCommand({
        TableName: KINGPIN_GOALS_TABLE,
        Key: marshall({ PK: `GOAL#${goalId}`, SK: REGISTRY_SK }),
        UpdateExpression: 'SET isActive = :isActive, updatedAt = :updatedAt, updatedBy = :updatedBy',
        ConditionExpression: 'attribute_exists(PK)',
        ExpressionAttributeValues: marshall({ ':isActive': false, ':updatedAt': now, ':updatedBy': updatedBy })
      })
    );
  }
}
